import React, { useState, useEffect } from 'react';
import { 
 MicrophoneIcon, 
 PlayIcon, 
 StopIcon,
 CheckCircleIcon,
 ClockIcon,
 ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import API_BASE_URL from '../Config/config';

const VoiceTraining = ({ userId, onTrainingComplete }) => {
 const [trainingStatus, setTrainingStatus] = useState(null);
 const [isRecording, setIsRecording] = useState(false);
 const [currentSample, setCurrentSample] = useState(0);
 const [recordedSamples, setRecordedSamples] = useState([]);
 const [isProcessing, setIsProcessing] = useState(false);
 const [isTraining, setIsTraining] = useState(false);
 const [voiceOptions, setVoiceOptions] = useState(null);

 const API_BASE = API_BASE_URL;

 const sampleTexts = [
"Hello, this is my voice sample for training.",
"I am recording this to create a personalized voice model.",
"This will be used to generate educational videos with my voice.",
"The system will learn my speech patterns and tone.",
"Thank you for helping me create custom content."
 ];

 useEffect(() => {
 if (userId) {
 fetchVoiceStatus();
 fetchVoiceOptions();
 }
 }, [userId]);

 const fetchVoiceStatus = async () => {
 try {
 const response = await fetch(`${API_BASE}/api/voice-training/status/${userId}`);
 const data = await response.json();
 if (data.success) {
 setTrainingStatus(data.data);
 }
 } catch (error) {
 console.error('Error fetching voice status:', error);
 }
 };

 const fetchVoiceOptions = async () => {
 try {
 const response = await fetch(`${API_BASE}/api/voice-training/options/${userId}`);
 const data = await response.json();
 if (data.success) {
 setVoiceOptions(data.data);
 }
 } catch (error) {
 console.error('Error fetching voice options:', error);
 }
 };

 const startVoiceTraining = async () => {
 try {
 const response = await fetch(`${API_BASE}/api/voice-training/start`, {
 method: 'POST',
 headers: {
 'Content-Type': 'application/json',
 },
 body: JSON.stringify({ userId }),
 });
 const data = await response.json();
 if (data.success) {
 setTrainingStatus(data.data);
 }
 } catch (error) {
 console.error('Error starting voice training:', error);
 }
 };

 const startRecording = () => {
 setIsRecording(true);
 // In a real implementation, you would use the Web Audio API
 // to record audio from the user's microphone
 console.log('Starting recording for sample:', currentSample);
 };

 const stopRecording = () => {
 setIsRecording(false);
 // In a real implementation, you would stop recording and save the audio
 console.log('Stopping recording for sample:', currentSample);
 
 // Simulate recording completion
 setRecordedSamples(prev => [...prev, {
 index: currentSample,
 text: sampleTexts[currentSample],
 recorded: true
 }]);
 
 if (currentSample < sampleTexts.length - 1) {
 setCurrentSample(prev => prev + 1);
 }
 };

 const uploadVoiceSample = async (audioBlob, sampleIndex) => {
 try {
 const formData = new FormData();
 formData.append('audio', audioBlob, `sample_${sampleIndex}.wav`);
 formData.append('sampleIndex', sampleIndex);

 const response = await fetch(`${API_BASE}/api/voice-training/upload/${userId}`, {
 method: 'POST',
 body: formData,
 });

 const data = await response.json();
 return data.success;
 } catch (error) {
 console.error('Error uploading voice sample:', error);
 return false;
 }
 };

 const processVoiceSamples = async () => {
 setIsProcessing(true);
 try {
 const response = await fetch(`${API_BASE}/api/voice-training/process`, {
 method: 'POST',
 headers: {
 'Content-Type': 'application/json',
 },
 body: JSON.stringify({ userId }),
 });
 const data = await response.json();
 if (data.success) {
 await fetchVoiceStatus();
 }
 } catch (error) {
 console.error('Error processing voice samples:', error);
 } finally {
 setIsProcessing(false);
 }
 };

 const trainVoiceModel = async () => {
 setIsTraining(true);
 try {
 const response = await fetch(`${API_BASE}/api/voice-training/train`, {
 method: 'POST',
 headers: {
 'Content-Type': 'application/json',
 },
 body: JSON.stringify({ userId }),
 });
 const data = await response.json();
 if (data.success) {
 await fetchVoiceStatus();
 await fetchVoiceOptions();
 if (onTrainingComplete) {
 onTrainingComplete();
 }
 }
 } catch (error) {
 console.error('Error training voice model:', error);
 } finally {
 setIsTraining(false);
 }
 };

 const getStatusIcon = () => {
 if (!trainingStatus) return <ClockIcon className="h-5 w-5 text-muted" />;
 
 if (trainingStatus.model_ready) {
 return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
 } else if (trainingStatus.has_recordings) {
 return <ExclamationTriangleIcon className="h-5 w-5 text-secondary" />;
 } else {
 return <ClockIcon className="h-5 w-5 text-muted" />;
 }
 };

 const getStatusText = () => {
 if (!trainingStatus) return 'Not started';
 
 if (trainingStatus.model_ready) {
 return 'Ready to use';
 } else if (trainingStatus.has_trained_model) {
 return 'Training completed';
 } else if (trainingStatus.has_processed_samples) {
 return 'Ready to train';
 } else if (trainingStatus.has_recordings) {
 return `${trainingStatus.recording_count} samples recorded`;
 } else {
 return 'Not started';
 }
 };

 if (!userId) {
 return (
 <div className="bg-bgSection p-6 rounded-lg">
 <p className="text-muted">Please log in to access voice training.</p>
 </div>
 );
 }

 return (
 <div className="bg-bgSection p-6 rounded-lg space-y-6">
 <div className="flex items-center justify-between">
 <h2 className="text-xl font-bold text-white">Custom Voice Training</h2>
 <div className="flex items-center gap-2">
 {getStatusIcon()}
 <span className="text-sm text-muted">{getStatusText()}</span>
 </div>
 </div>

 {/* Voice Options */}
 {voiceOptions && (
 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
 <div className="bg-gray-700 p-4 rounded-lg">
 <h3 className="font-semibold text-white mb-2">{voiceOptions.default_tts.name}</h3>
 <p className="text-sm text-muted mb-2">{voiceOptions.default_tts.description}</p>
 <span className="text-xs text-green-400">✓ Available</span>
 </div>
 <div className="bg-gray-700 p-4 rounded-lg">
 <h3 className="font-semibold text-white mb-2">{voiceOptions.custom_voice.name}</h3>
 <p className="text-sm text-muted mb-2">{voiceOptions.custom_voice.description}</p>
 <span className={`text-xs ${voiceOptions.custom_voice.available ? 'text-green-400' : 'text-primary'}`}>
 {voiceOptions.custom_voice.available ? '✓ Ready' : '⚠ ' + voiceOptions.custom_voice.status}
 </span>
 </div>
 </div>
 )}

 {/* Training Steps */}
 {!trainingStatus?.model_ready && (
 <div className="space-y-4">
 <h3 className="text-lg font-semibold text-white">Training Steps</h3>
 
 {/* Step 1: Record Samples */}
 <div className="bg-gray-700 p-4 rounded-lg">
 <h4 className="font-medium text-white mb-3">Step 1: Record Voice Samples</h4>
 <p className="text-sm text-muted mb-4">
 Record yourself reading the following texts. Speak clearly and naturally.
 </p>
 
 {currentSample < sampleTexts.length && (
 <div className="space-y-4">
 <div className="bg-gray-600 p-3 rounded">
 <p className="text-white text-sm">
 Sample {currentSample + 1} of {sampleTexts.length}:
 </p>
 <p className="text-gray-200 mt-2">"{sampleTexts[currentSample]}"</p>
 </div>
 
 <div className="flex gap-3">
 {!isRecording ? (
 <button
 onClick={startRecording}
 className="flex items-center gap-2 bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg transition-colors"
 >
 <MicrophoneIcon className="h-4 w-4" />
 Start Recording
 </button>
 ) : (
 <button
 onClick={stopRecording}
 className="flex items-center gap-2 bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg transition-colors"
 >
 <StopIcon className="h-4 w-4" />
 Stop Recording
 </button>
 )}
 </div>
 </div>
 )}
 
 {recordedSamples.length > 0 && (
 <div className="mt-4">
 <p className="text-sm text-muted mb-2">Recorded samples:</p>
 <div className="space-y-2">
 {recordedSamples.map((sample, index) => (
 <div key={index} className="flex items-center gap-2 text-sm">
 <CheckCircleIcon className="h-4 w-4 text-green-500" />
 <span className="text-muted">Sample {sample.index + 1}</span>
 </div>
 ))}
 </div>
 </div>
 )}
 </div>

 {/* Step 2: Process Samples */}
 {recordedSamples.length === sampleTexts.length && !trainingStatus?.has_processed_samples && (
 <div className="bg-gray-700 p-4 rounded-lg">
 <h4 className="font-medium text-white mb-3">Step 2: Process Voice Samples</h4>
 <p className="text-sm text-muted mb-4">
 Process and prepare your voice samples for training.
 </p>
 <button
 onClick={processVoiceSamples}
 disabled={isProcessing}
 className="bg-blue-500 hover:bg-blue-600 disabled:bg-gray-500 text-white px-4 py-2 rounded-lg transition-colors"
 >
 {isProcessing ? 'Processing...' : 'Process Samples'}
 </button>
 </div>
 )}

 {/* Step 3: Train Model */}
 {trainingStatus?.has_processed_samples && !trainingStatus?.has_trained_model && (
 <div className="bg-gray-700 p-4 rounded-lg">
 <h4 className="font-medium text-white mb-3">Step 3: Train Voice Model</h4>
 <p className="text-sm text-muted mb-4">
 Train your personalized voice model. This may take a few minutes.
 </p>
 <button
 onClick={trainVoiceModel}
 disabled={isTraining}
 className="bg-green-500 hover:bg-green-600 disabled:bg-gray-500 text-white px-4 py-2 rounded-lg transition-colors"
 >
 {isTraining ? 'Training...' : 'Train Model'}
 </button>
 </div>
 )}
 </div>
 )}

 {/* Success Message */}
 {trainingStatus?.model_ready && (
 <div className="bg-green-900 border border-green-700 p-4 rounded-lg">
 <div className="flex items-center gap-2 mb-2">
 <CheckCircleIcon className="h-5 w-5 text-green-400" />
 <h4 className="font-medium text-green-400">Voice Training Complete!</h4>
 </div>
 <p className="text-sm text-green-300">
 Your custom voice model is ready. You can now generate videos with your own voice.
 </p>
 </div>
 )}
 </div>
 );
};

export default VoiceTraining;









